import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ChatBot from "./ChatBot";
import axios from "axios";
import CharacterDisplay from "../CharacterDisplay";
import { t } from "i18next";

const DocumentView = ({ dir }) => {

  const location = useLocation();
  const navigate = useNavigate();

  const documentUrl = location.state?.docUrl || null;
  const documentName = location.state?.name || null;
  const courseId = location.state?.courseId || null;
  const moduleId = location.state?.moduleId || null;
  const indexDoc = location.state?.index;

  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 10000);
    return () => clearTimeout(timer);
  }, [documentUrl]);

  useEffect(() => {
    const updateSidebarState = () => {
      const sidebarState = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-open');
      setSidebarOpen(sidebarState === '1');
    };
    updateSidebarState();
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    const observer = new MutationObserver(updateSidebarState);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getContentPadding = () => {
    if (isMobile) {
      return "";
    }
    return sidebarOpen
      ? (dir === 'rtl' ? 'pr-64' : 'pl-64')
      : (dir === 'rtl' ? 'pr-20' : 'pl-20');
  };

  const handleComplete = async () => {
    setGenerating(true);
    try {
      const base_url = process.env.REACT_APP_LMS_APP_BASE_URL;
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const profileData = JSON.parse(localStorage.getItem("profileData") || sessionStorage.getItem("profileData"));
      const userId = profileData?.id;
      const response = await axios.put(
        `${base_url}/module/updateDocumentProgress`,
        {
          userId,
          moduleId,
          courseId,
          documentIndex: indexDoc,
          status: "completed",
          timeSpent: 300,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("Document progress updated:", response.data);
      setGenerating(false);
      navigate(`/lms/course`, { state: { moduleId } });
    } catch (error) {
      setGenerating(false);
      // if (error?.response?.status === 401) handleSignOut();
      console.error("Error updating progress:", error);
    }
  };

  if (!documentUrl) {
    return (
      <div className="text-center py-10 text-red-500">
        No document URL provided.
      </div>
    );
  }

  const ext = documentUrl.split(".").pop().toLowerCase();
  const isPDF = ext === "pdf";
  const isDoc = ext === "doc" || ext === "docx";
  const isPpt = ext === "ppt" || ext === "pptx";

  return (
    <div dir={dir} className={`flex flex-col md:flex-row h-[84vh] m-4 transition-all duration-300 ${getContentPadding()}`}>
      {/* Document Viewer */}
      <div className="flex-1 flex flex-col">
        <header className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50 pb-6 z-10">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              style={{ backgroundColor: "#767676" }}
              className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center transition-all duration-300 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl text-white" />
            </button>
            <div>
              <h2 className="text-xl font-medium text-gray-900">{documentName}</h2>
            </div>
          </div>
        </header>

        <div className="relative flex-1 flex flex-col bg-gray-100">
          <main className="relative flex-1 overflow-hidden flex items-center justify-center bg-gray-50">
            <div className="w-full h-[90%] bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200 relative z-10 overflow-y-auto mx-6">
              {loading && (
                <div className="absolute inset-0 flex flex-col justify-center items-center bg-white/90 backdrop-blur-sm z-30">
                  <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-gray-600 font-medium">Loading document...</p>
                </div>
              )}
              {isPDF ? (
                <div className="relative w-full h-full">
                  <iframe
                    ref={iframeRef}
                    src={`${documentUrl}#page=1&view=FitH`}
                    className="w-full h-full border-none"
                    title="PDF Viewer"
                    onLoad={() => setLoading(false)}
                  />
                  <button
                    onClick={() => iframeRef.current?.requestFullscreen()}
                    className="absolute bottom-4 right-8 z-50 bg-black/70 text-white px-3 py-1 rounded"
                  >
                    ⛶
                  </button>
                </div>
              ) : isDoc || isPpt ? (
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                    documentUrl
                  )}`}
                  className="w-full h-full border-none overflow-hidden"
                  title="Office Document"
                  allowFullScreen={true}
                  onLoad={() => setLoading(false)}
                />
              ) : (
                <a
                  href={documentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline block text-center mt-20"
                >
                  Open Document
                </a>
              )}
            </div>
          </main>
        </div>
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleComplete}
            className="px-4 py-2 text-white rounded-full bg-[#B03982]"
          >
            {generating ? t("Verifying...") : t("Mark as Completed")}
          </button>
        </div>
      </div>
      <div className="w-1/3 flex flex-col justify-center items-center relative">
        <div className="absolute bottom-0 right-0 hidden md:flex flex-col items-center space-y-4">
          <div className="w-[12rem] h-[18rem]">
            <CharacterDisplay loading={loading} />
          </div>
        </div>
      </div>
      <ChatBot dir={dir} showChat={showChat} setShowChat={setShowChat} />
    </div>
  );
};

export default DocumentView;